import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Fade, Avatar, Button } from '@mui/material';
import {
  PrecisionManufacturing, Inventory2, LocalShipping,
  Timer, Warehouse, Home, Storefront, AccountBalance,
  ViewKanban, Assignment, WhatsApp, SmartToy
} from '@mui/icons-material';
import KuhkopfLogo from '../assets/images/ALPINOR_LOGO_FINAL.png';

const iconMatrix = [
  { label: 'Produktion', icon: <PrecisionManufacturing />, color: '#f59e0b' },
  { label: 'Verpackung', icon: <Inventory2 />, color: '#8b5cf6' },
  { label: 'Versand', icon: <LocalShipping />, color: '#3b82f6' },
  { label: 'Arbeitszeit', icon: <Timer />, color: '#10b981' },
  { label: 'Lager', icon: <Warehouse />, color: '#ef4444' },
  { label: 'Allgemein', icon: <Home />, color: '#3b82f6' },
  { label: 'CRM & Sales', icon: <Storefront />, color: '#10b981' },
  { label: 'Finanzen', icon: <AccountBalance />, color: '#06b6d4' },
  { label: 'Aufgaben', icon: <ViewKanban />, color: '#d946ef' },
  { label: 'Whiteboard', icon: <Assignment />, color: '#64748b' },
  { label: 'Chatroom', icon: <WhatsApp />, color: '#22c55e' },
  { label: 'Chatbot', icon: <SmartToy />, color: '#00d2ff' }
];

const SystemLaunch = ({ onComplete }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    // Icon Reveal Sequence
    const totalIcons = iconMatrix.length;
    const revealDuration = 800; 
    const intervalTime = revealDuration / totalIcons; 

    const timer = setInterval(() => {
      setVisibleCount(prev => {
        if (prev < totalIcons) return prev + 1;
        clearInterval(timer);
        setTimeout(() => setIsFinished(true), 300); 
        return prev;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: '#ffffff',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center', // Center content perfectly
        overflow: 'hidden',
        p: 2
      }}
    >
      <Box sx={{ 
        zIndex: 10, width: '100%', maxWidth: 600, textAlign: 'center',
        display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' 
      }}>
        <Fade in timeout={800}>
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ height: { xs: 120, md: 160 }, mb: 1 }}>
              <img src={KuhkopfLogo} alt="Logo" style={{ height: '100%' }} />
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#94a3b8', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', mb: 0.2,
                fontSize: '0.6rem'
              }}
            >
              Herzlich willkommen bei der
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 900, color: '#1e293b', letterSpacing: -1,
                fontSize: { xs: '1.4rem', md: '2.2rem' }
              }}
            >
              Seifenmanufaktur Allgäu
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={1.5} justifyContent="center" sx={{ mb: 3 }}>
          {iconMatrix.map((item, index) => (
            <Grid item key={index} xs={4}>
              <Fade in={index < visibleCount} timeout={600}>
                <Box sx={{ 
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                  opacity: 0,
                  transform: index < visibleCount ? 'scale(1)' : 'scale(0.5)',
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: `${index * 50}ms`
                }}>
                  <Box 
                    className={`lottie-icon-${index}`}
                    sx={{ 
                      color: '#1e293b', 
                      width: 50, height: 50, 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 0.5
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#94a3b8', fontWeight: 800, fontSize: '0.55rem', 
                      textTransform: 'uppercase', letterSpacing: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>

        <Fade in={isFinished}>
          <Box>
            <Button 
              variant="contained" 
              onClick={onComplete}
              size="large"
              sx={{ 
                bgcolor: '#1e293b', color: '#fff', borderRadius: 10, px: 6, py: 1.5,
                fontWeight: 900, textTransform: 'none', fontSize: '1rem',
                boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#0f172a', transform: 'translateY(-2px)' },
                transition: 'all 0.3s ease'
              }}
            >
              Weiter zur App
            </Button>
          </Box>
        </Fade>
      </Box>

      <style>{`
        /* Lottie-style individual animations */
        .lottie-icon-0 svg { animation: rotateArm 3s ease-in-out infinite; }
        
        /* PulseBox animation for requested icons */
        .lottie-icon-1 svg, .lottie-icon-3 svg, .lottie-icon-6 svg, 
        .lottie-icon-7 svg, .lottie-icon-8 svg, .lottie-icon-9 svg,
        .lottie-icon-10 svg, .lottie-icon-11 svg 
        { animation: pulseBox 2s ease-in-out infinite; }
        
        .lottie-icon-2 svg { animation: driveTruck 2.5s ease-in-out infinite; }
        .lottie-icon-4 svg { animation: warehouseFloat 3s ease-in-out infinite; }
        .lottie-icon-5 svg { animation: bounceHome 2s ease-in-out infinite; }
        
        @keyframes rotateArm {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(15deg) translateX(5px); }
        }
        @keyframes pulseBox {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes driveTruck {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(8px); }
        }
        @keyframes warehouseFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes bounceHome {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px) scaleX(1.1); }
        }
      `}</style>
    </Box>
  );
};

export default SystemLaunch;
